import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Printer, Download } from "lucide-react";
import WolfCrest from "@/components/WolfCrest";
import { trackView } from "@/lib/api";
import {
  RESOLUTIONS,
  SHARED_HEADING,
  SHARED_WHEREAS,
  IMPLEMENTATION_CLAUSE,
  FINALLY_CLAUSE,
  CERTIFICATION,
} from "@/data/resolutions";

export default function ResolutionPage() {
  const { slug } = useParams();
  const res = RESOLUTIONS.find((r) => r.slug === slug);

  useEffect(() => {
    if (!res) return;
    const key = `resolution:${slug}`;
    const seen = JSON.parse(sessionStorage.getItem("views_tracked") || "[]");
    if (!seen.includes(key)) {
      sessionStorage.setItem("views_tracked", JSON.stringify([...seen, key]));
      trackView(key).catch(() => {});
    }
  }, [slug, res]);

  if (!res) return <Navigate to="/" replace />;

  return (
    <main className="min-h-screen bg-[#F8F9FA]" data-testid="resolution-page">
      <header className="bg-navy text-[#F8F9FA] px-5 md:px-10 py-4 flex items-center justify-between print:hidden">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-gold transition-colors"
          data-testid="resolution-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="flex items-center gap-3">
          <WolfCrest className="w-7 h-7" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/70">Resolution Solutions</span>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-electric mb-4">
          Resolution · Shared Vision §{res.n} · Drafted &amp; Ready
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy mb-8" data-testid="resolution-title">
          {res.title}
        </h1>

        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-navy/60 leading-relaxed mb-4">
          {SHARED_HEADING}
        </p>
        <p className="text-navy/70 text-base md:text-lg leading-relaxed italic border-l-2 border-gold pl-5 mb-10">
          A Resolution Entitled: &ldquo;{res.entitled}&rdquo;
        </p>

        <h2 className="text-xs uppercase tracking-[0.3em] font-bold text-navy mb-6 border-b border-navy/15 pb-3">
          Whereas
        </h2>
        <div className="space-y-4 mb-10">
          {[...SHARED_WHEREAS, ...res.extraWhereas].map((w, i) => (
            <p key={i} className="text-navy/80 text-base md:text-lg leading-relaxed" data-testid={`resolution-whereas-${i + 1}`}>
              <span className="font-bold text-navy">WHEREAS, </span>{w}
            </p>
          ))}
        </div>

        <h2 className="text-xs uppercase tracking-[0.3em] font-bold text-navy mb-6 border-b border-navy/15 pb-3">
          Now, Therefore, Be It Resolved
        </h2>
        <div className="space-y-6">
          {res.resolved.map((c, i) => (
            <p key={i} className="text-navy/85 text-base md:text-lg leading-relaxed" data-testid={`resolution-clause-${i + 1}`}>
              <span className="font-bold text-navy">{i === 0 ? "NOW, THEREFORE, BE IT RESOLVED, " : "BE IT FURTHER RESOLVED, "}</span>
              {c}
            </p>
          ))}
          <p className="text-navy/85 text-base md:text-lg leading-relaxed">
            <span className="font-bold text-navy">BE IT FURTHER RESOLVED, </span>{IMPLEMENTATION_CLAUSE}
          </p>
          <p className="text-navy/85 text-base md:text-lg leading-relaxed">
            <span className="font-bold text-navy">BE IT FINALLY RESOLVED, </span>{FINALLY_CLAUSE}
          </p>
        </div>

        <h2 className="text-xs uppercase tracking-[0.3em] font-bold text-navy mt-12 mb-4 border-b border-navy/15 pb-3">
          Certification
        </h2>
        <p className="text-navy/70 text-sm md:text-base leading-relaxed mb-10">{CERTIFICATION}</p>

        <div className="mt-12 pt-8 border-t border-navy/15 flex flex-col sm:flex-row gap-4 print:hidden">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-700 text-white text-xs uppercase tracking-[0.2em] font-bold px-6 py-3.5 rounded-full transition-colors"
            data-testid="resolution-print-btn"
          >
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
          <a
            href={res.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 text-navy hover:text-electric border border-navy/30 hover:border-electric/60 text-xs uppercase tracking-[0.2em] font-bold px-6 py-3.5 rounded-full transition-colors"
            data-testid="resolution-download-btn"
          >
            <Download className="w-4 h-4" /> Download original
          </a>
        </div>
      </article>
    </main>
  );
}
